import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Maximize2, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ExpandViewProps {
    title: string
    caption?: string
    ratio?: number
    children: ReactNode
    className?: string
}

const ExpandView: FC<ExpandViewProps> = ({
    title,
    caption,
    ratio = 2.2,
    children,
    className
}): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return
        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(true)}
                aria-label={`View larger: ${title}`}
                aria-haspopup='dialog'
                className={cn(
                    'z-10 grid size-10 place-items-center rounded-full border border-border bg-card text-foreground shadow-[0_8px_20px_-8px_rgb(2_6_23/0.4)] transition-transform duration-200 hover:scale-105',
                    className
                )}
            >
                <Maximize2 className='size-4' strokeWidth={2} />
            </button>

            <dialog
                ref={dialogRef}
                aria-label={title}
                onClose={() => setOpen(false)}
                onClick={(event) => {
                    if (event.target === event.currentTarget) setOpen(false)
                }}
                className='fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none bg-transparent p-4 text-white backdrop:bg-slate-950/80 backdrop:backdrop-blur-sm open:flex open:items-center open:justify-center sm:p-8'
            >
                {open && (
                    <div className='flex w-full max-w-6xl flex-col items-center gap-5 animate-in fade-in zoom-in-95 duration-300'>
                        <div className='flex w-full items-center justify-between gap-4'>
                            <p className='font-heading text-base font-bold sm:text-lg'>
                                {title}
                            </p>

                            <button
                                type='button'
                                onClick={() => setOpen(false)}
                                aria-label='Close'
                                className='grid size-10 shrink-0 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20'
                            >
                                <X className='size-5' />
                            </button>
                        </div>

                        <div className='w-full' style={{ maxWidth: `calc((100dvh - 11rem) * ${ratio})` }}>
                            {children}
                        </div>

                        {caption && (
                            <p className='text-center text-sm text-white/70'>
                                {caption}
                            </p>
                        )}
                    </div>
                )}
            </dialog>
        </>
    )
}

export default ExpandView
